import { useEffect, useState } from 'react';
import { CHART, CHART_SERIES } from '../../../lib/chartColors';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { LineChart, BarChart, PieChart, Users, ArrowUpRight, Activity, Building2, Briefcase, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/Button';
import apiClient from '../../../lib/api';
import {
  ResponsiveContainer, PieChart as RechartsPie, Pie, Cell,
  BarChart as RechartsBar, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart as RechartsLine, Line,
} from 'recharts';

interface ReportsData {
  headcount: number;
  departmentBreakdown: { department: string; count: number }[];
  statusBreakdown: { status: string; count: number }[];
  attritionRate: number;
  avgTenureYears: number;
  joinedLast90Days: number;
}

const COLORS = CHART_SERIES;

export function HrReports() {
  const [data, setData] = useState<ReportsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient.get('/people/reports')
      .then((res) => { if (active) setData(res.data?.data ?? res.data); })
      .catch((e) => { if (active) setError(e?.response?.data?.message || e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading reports…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <AlertCircle className="w-6 h-6 mb-2" /> {error}
      </div>
    );
  }
  if (!data) return null;

  const deptData = data.departmentBreakdown.length
    ? data.departmentBreakdown
    : [{ department: 'No data', count: 0 }];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">HR Reports</h1>
          <p className="text-muted-foreground">Headcount, attrition, and diversity analytics.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}>Export PDF</Button>
          <Button variant="primary" onClick={() => window.location.reload()}>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Headcount</p>
                <h3 className="text-foreground font-display text-metric">{data.headcount}</h3>
              </div>
              <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                <Users className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-center text-sm text-success">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span>+{data.joinedLast90Days} joined (90d)</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Attrition Rate</p>
                <h3 className="text-foreground font-display text-metric">{data.attritionRate}%</h3>
              </div>
              <div className="w-8 h-8 rounded bg-destructive/10 flex items-center justify-center text-destructive">
                <Activity className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <span>Trailing 12 months</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Departments</p>
                <h3 className="text-foreground font-display text-metric">{data.departmentBreakdown.length}</h3>
              </div>
              <div className="w-8 h-8 rounded bg-warning/10 flex items-center justify-center text-warning">
                <Building2 className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <span>Active functions</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Tenure</p>
                <h3 className="text-foreground font-display text-metric">{data.avgTenureYears} yrs</h3>
              </div>
              <div className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-foreground">
                <Briefcase className="w-4 h-4" />
              </div>
            </div>
            <div className="flex items-center text-sm text-success">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span>Across workforce</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="h-80 flex flex-col">
          <CardHeader>
            <h3 className="text-foreground flex items-center gap-2 font-display text-section-title">
              <PieChart className="w-4 h-4 text-primary" /> Department Breakdown
            </h3>
          </CardHeader>
          <CardContent className="flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie data={deptData} dataKey="count" nameKey="department" outerRadius={90} label>
                  {deptData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="h-80 flex flex-col">
          <CardHeader>
            <h3 className="text-foreground flex items-center gap-2 font-display text-section-title">
              <BarChart className="w-4 h-4 text-primary" /> Headcount by Department
            </h3>
          </CardHeader>
          <CardContent className="flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBar data={deptData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill={CHART.blue} radius={[4, 4, 0, 0]} />
              </RechartsBar>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="h-80 flex flex-col">
        <CardHeader>
          <h3 className="text-foreground flex items-center gap-2 font-display text-section-title">
            <LineChart className="w-4 h-4 text-primary" /> Employment Status
          </h3>
        </CardHeader>
        <CardContent className="flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLine data={data.statusBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="status" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={CHART.blue} strokeWidth={2} />
            </RechartsLine>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
